import React, { useCallback } from 'react';
import { formatDistanceToNow } from 'date-fns';
import type { DynamicWidgetConfig, QueryResult } from '@/types/dashboard';
import type { VisualizationClickEvent } from './DynamicVisualization';

interface ListVisualizationProps {
  data: QueryResult;
  config: DynamicWidgetConfig;
  /** Callback when a list item is clicked (DASH-24.2) */
  onSegmentClick?: (event: VisualizationClickEvent) => void;
}

/**
 * List Visualization (DASH-8.2, DASH-24.2)
 * Shows simple item list with limit and formatting
 * DASH-24.2: Added item click handler for drill-down
 */
export function ListVisualization({ data, config, onSegmentClick }: ListVisualizationProps) {
  // Expect array of records
  const records = Array.isArray(data.data) ? data.data : [];
  const limit = config.aggregation.limit || 10;
  const displayedRecords = records.slice(0, limit);
  const hasMore = records.length > limit;

  // Handle item click (DASH-24.2)
  const handleItemClick = useCallback(
    (record: any, index: number) => {
      if (onSegmentClick) {
        onSegmentClick({
          value: record.id || index,
          index,
          data: record,
        });
      }
    },
    [onSegmentClick]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 overflow-auto space-y-2">
        {displayedRecords.length === 0 ? (
          <div className="text-center text-muted-foreground py-8">
            No items found
          </div>
        ) : (
          displayedRecords.map((record, index) => (
            <ListItem
              key={index}
              record={record}
              dataSource={config.dataSource}
              onClick={() => handleItemClick(record, index)}
            />
          ))
        )}
      </div>

      {/* View All link if more items exist */}
      {hasMore && (
        <div className="pt-4 border-t">
          <div className="text-sm text-muted-foreground text-center">
            Showing {displayedRecords.length} of {records.length} items
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Individual list item component (DASH-24.2: Added onClick handler)
 */
function ListItem({ record, dataSource, onClick }: { record: any; dataSource: string; onClick?: () => void }) {
  const { title, subtitle, timestamp } = formatListItem(record, dataSource);

  return (
    <div
      className="p-3 rounded-md bg-muted/50 hover:bg-muted transition-colors cursor-pointer"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && onClick) {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <div className="font-medium text-foreground">
        {title}
      </div>
      {subtitle && (
        <div className="text-sm text-muted-foreground mt-1">
          {subtitle}
        </div>
      )}
      {timestamp && (
        <div className="text-xs text-muted-foreground mt-1">
          {(() => {
            try {
              return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
            } catch {
              return timestamp;
            }
          })()}
        </div>
      )}
    </div>
  );
}

/**
 * Format record into list item display
 */
function formatListItem(record: any, dataSource: string): {
  title: string;
  subtitle?: string;
  timestamp?: string;
} {
  switch (dataSource) {
    case 'projects':
      return {
        title: record.project_name || 'Unnamed Project',
        subtitle: `${record.project_status || 'No status'} · ${record.company_name || 'No company'}`,
        timestamp: record.created_at,
      };

    case 'appointments':
      return {
        title: record.lead_name || 'Unnamed Lead',
        subtitle: `${record.disposition || 'No disposition'}`,
        timestamp: record.date_time,
      };

    case 'tasks':
      return {
        title: record.title || 'Unnamed Task',
        subtitle: `${record.status || 'No status'} · Due ${record.due_date ? (() => {
          try {
            return formatDistanceToNow(new Date(record.due_date), { addSuffix: true });
          } catch {
            return 'Invalid date';
          }
        })() : 'No due date'}`,
        timestamp: record.created_at,
      };

    case 'notifications':
      return {
        title: record.title || 'Notification',
        subtitle: record.message,
        timestamp: record.created_at,
      };

    default:
      return {
        title: record.id || 'Item',
        subtitle: record.name || record.title,
        timestamp: record.created_at,
      };
  }
}
